import {
  OpenInNew as OpenInNewIcon,
  PowerSettingsNew as PowerIcon,
} from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { NoSettingsCard } from "../NoSettingsCard.js";
import { useTranslate } from "@tolgee/react";
import { useUserData } from "../../../../auth/useUserData.js";
import type { IntegrationSettingsProps } from "../../integrationRegistry.js";
import { getIntegrationMeta } from "../../integrationRegistry.js";
import { SettingsPageLayout } from "./SettingsPageLayout.js";
import { useIntegrationSettingsBase } from "./useIntegrationSettingsBase.js";
import { integrationErrorMessage } from "../../integrationErrorMessage.js";

export const PdfGeneratorSettings = ({
  integrationId,
  mode,
}: IntegrationSettingsProps) => {
  const { t } = useTranslate("Integrations");
  const meta = getIntegrationMeta(integrationId);
  const viewer = useUserData().currentUser!;
  const {
    orgIntegration,
    refetchDashboard,
    loading,
    removeLoading,
    handleAdd,
    handleDisconnect,
    enqueueSnackbar,
  } = useIntegrationSettingsBase(integrationId, mode);

  const integrationName = meta.sidebarLabel ?? "PDF Generator";

  if (mode === "create") {
    return (
      <SettingsPageLayout
        integrationId={integrationId}
        integrationName="PDF Generator"
        title={`${t("Connect")} PDF Generator`}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            px: 2,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              backgroundColor: meta.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              {meta.shortName}
            </Typography>
          </Box>
          <Typography variant="h6" gutterBottom>
            PDF Generator
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 420, textAlign: "center" }}
          >
            {t(meta.description as any)}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<PowerIcon />}
            onClick={async () => {
              try {
                await handleAdd();
              } catch (e) {
                enqueueSnackbar(
                  integrationErrorMessage(
                    e,
                    t("Settings could not be saved. Please check your input.")
                  ),
                  { variant: "error" }
                );
              }
            }}
          >
            {t("Connect")}
          </Button>
        </Box>
      </SettingsPageLayout>
    );
  }

  return (
    <SettingsPageLayout
      integrationId={integrationId}
      integrationName={integrationName}
      title={`${integrationName} — ${t("Settings")}`}
      onDisconnect={handleDisconnect}
      removeLoading={removeLoading}
      loading={loading && !orgIntegration}
      events={orgIntegration?.events ?? []}
      onRefreshEvents={refetchDashboard}
      hasDashboard={!!orgIntegration?.health}
    >
      <NoSettingsCard
        description={t("Templates are managed in the PDF dashboard.")}
      >
        <Button
          variant="outlined"
          endIcon={<OpenInNewIcon />}
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          href={`${import.meta.env.VITE_PDF_DASHBOARD_URL}?environment=${import.meta.env.VITE_DEPLOY_ENV}&selectedUserId=${viewer.id}&initialPath=/dashboard`}
        >
          {t("Go to templates dashboard")}
        </Button>
      </NoSettingsCard>
    </SettingsPageLayout>
  );
};
